import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useStore, useServers, useCustomers } from '../store/useStore';

function StatusBadge({ status }: { status: 'online' | 'offline' }) {
  return (
    <span className={clsx('badge', status === 'online' ? 'badge-success' : 'badge-error')}>
      {status}
    </span>
  );
}

function formatTimeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

export default function Servers() {
  const { servers, isLoading, error } = useServers();
  const { customers } = useCustomers();
  const fetchServers = useStore((state) => state.fetchServers);
  const fetchCustomers = useStore((state) => state.fetchCustomers);
  const [filterCustomerId, setFilterCustomerId] = useState<number | undefined>(undefined);

  useEffect(() => {
    fetchServers();
    fetchCustomers();
    const interval = setInterval(() => fetchServers(filterCustomerId), 30000);
    return () => clearInterval(interval);
  }, [fetchServers, fetchCustomers, filterCustomerId]);

  const handleFilterChange = (customerId: number | undefined) => {
    setFilterCustomerId(customerId);
    fetchServers(customerId);
  };

  const filteredServers = filterCustomerId
    ? servers.filter((s) => s.customer_id === filterCustomerId)
    : servers;

  const onlineCount = filteredServers.filter((s) => s.status === 'online').length;
  const totalAgents = filteredServers.reduce((sum, s) => sum + s.agent_count, 0);
  const totalBlades = filteredServers.reduce((sum, s) => sum + s.blade_count, 0);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-text-primary">Registered Servers</h1>
        <div className="flex items-center gap-3">
          <select
            className="bg-card-bg border border-white/10 rounded-md px-3 py-1.5 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
            value={filterCustomerId ?? ''}
            onChange={(e) => handleFilterChange(e.target.value ? Number(e.target.value) : undefined)}
          >
            <option value="">All Customers</option>
            {customers
              .filter((c) => c.role === 'customer')
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.display_name || c.username}
                </option>
              ))}
          </select>
          <button
            type="button"
            onClick={() => fetchServers(filterCustomerId)}
            className="btn btn-secondary text-sm"
            disabled={isLoading}
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <p className="text-sm text-text-secondary mb-1">Total Servers</p>
          <p className="text-2xl font-bold text-text-primary">{filteredServers.length}</p>
        </div>
        <div className="card p-4">
          <p className="text-sm text-text-secondary mb-1">Online</p>
          <p className="text-2xl font-bold text-status-green">{onlineCount}</p>
        </div>
        <div className="card p-4">
          <p className="text-sm text-text-secondary mb-1">Total Agents</p>
          <p className="text-2xl font-bold text-text-primary">{totalAgents}</p>
        </div>
        <div className="card p-4">
          <p className="text-sm text-text-secondary mb-1">Total Blades</p>
          <p className="text-2xl font-bold text-text-primary">{totalBlades}</p>
        </div>
      </div>

      {error && (
        <div className="card p-4 border-status-red/30 bg-status-red/5">
          <p className="text-sm text-status-red">{error}</p>
        </div>
      )}

      {/* Servers table */}
      {isLoading && filteredServers.length === 0 ? (
        <div className="card p-8 text-center">
          <p className="text-text-secondary">Loading servers...</p>
        </div>
      ) : filteredServers.length === 0 ? (
        <div className="card p-8 text-center">
          <p className="text-text-secondary">No servers registered for linking.</p>
          <p className="text-sm text-dimmed mt-1">
            Layers Manage instances will appear here once they register with the license API.
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="grid grid-cols-[1fr_1fr_120px_1fr_100px_80px_80px_80px_120px] gap-3 items-center p-3 bg-dark-bg border-b border-white/10 text-xs font-medium text-text-secondary uppercase tracking-wider">
            <span>Customer</span>
            <span>Server Name</span>
            <span>Site ID</span>
            <span>Host</span>
            <span>Version</span>
            <span className="text-right">Agents</span>
            <span className="text-right">Blades</span>
            <span>Status</span>
            <span>Last Seen</span>
          </div>

          {filteredServers.map((server) => (
            <div
              key={server.id}
              className="grid grid-cols-[1fr_1fr_120px_1fr_100px_80px_80px_80px_120px] gap-3 items-center p-3 border-b border-white/5 hover:bg-white/[0.02] transition-colors"
            >
              <span className="text-sm text-text-primary font-medium truncate" title={server.customer_username}>
                {server.customer_display_name || server.customer_username}
              </span>
              <span className="text-sm text-text-primary truncate" title={server.name}>
                {server.name || '-'}
              </span>
              <span className="text-sm text-text-secondary font-mono">{server.site_id || '-'}</span>
              <span className="text-sm text-text-secondary font-mono truncate" title={`${server.public_host}:${server.https_port}`}>
                {server.public_host}{server.https_port !== 443 ? `:${server.https_port}` : ''}
              </span>
              <span className="text-sm text-text-secondary">{server.version || '-'}</span>
              <span className="text-sm text-text-primary text-right">{server.agent_count}</span>
              <span className="text-sm text-text-primary text-right">{server.blade_count}</span>
              <StatusBadge status={server.status} />
              <span className="text-sm text-text-secondary" title={new Date(server.last_heartbeat).toLocaleString()}>
                {formatTimeAgo(server.last_heartbeat)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
